const USERS_RECORDS_PATH = '/collections/users/records';
const AUTH_WITH_PASSWORD_PATH = '/collections/users/auth-with-password';

export const STAFF_ROLES = ['owner', 'manager', 'admin', 'customer_care'];
export const STAFF_ACCESS_ROLES = ['owner', 'manager', 'admin'];

export function getApiBaseUrl() {
  return (process.env.REACT_APP_CARE_API_URL || 'https://api.nvoisyshealth.in').replace(/\/$/, '');
}

function requireApiBaseUrl() {
  const base = getApiBaseUrl();
  if (!base) {
    throw new Error(
      'REACT_APP_CARE_API_URL is not set. Add it to your environment to use Care API auth.'
    );
  }
  return base;
}

function isValidStaffRole(role) {
  return STAFF_ROLES.includes(role);
}

function normalizeUserRecord(record) {
  const fallbackRole = 'customer_care';
  const role = isValidStaffRole(record?.role) ? record.role : fallbackRole;
  return {
    id: record?.id,
    name: record?.name || '',
    email: record?.email || '',
    phone: record?.phone || '',
    role,
    collectionId: record?.collectionId,
    collectionName: record?.collectionName,
    avatar: record?.avatar || '',
    created: record?.created,
    updated: record?.updated,
    verified: Boolean(record?.verified)
  };
}

async function apiRequest(path, options = {}) {
  const base = requireApiBaseUrl();
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.message || `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload;
}

/** Care API auth tokens are JWTs; payload includes the user record `id`. */
function getUserIdFromCareApiToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const payload = JSON.parse(atob(base64));
    return payload?.id || null;
  } catch {
    return null;
  }
}

export async function signInWithPassword({ identity, password }) {
  if (!identity || !password) {
    throw new Error('Email and password are required.');
  }

  const payload = await apiRequest(AUTH_WITH_PASSWORD_PATH, {
    method: 'POST',
    body: JSON.stringify({ identity, password })
  });
  return {
    token: payload.token,
    user: normalizeUserRecord(payload.record)
  };
}

export async function restoreSessionWithToken({ token, user }) {
  if (!token) {
    throw new Error('Missing token');
  }

  const userId = user?.id || getUserIdFromCareApiToken(token);
  if (!userId) {
    throw new Error('Missing user id');
  }

  const profile = await getMyProfile(userId, token);
  return { token, user: profile };
}

export async function getMyProfile(userId, token) {
  const payload = await apiRequest(`${USERS_RECORDS_PATH}/${userId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` }
  });
  return normalizeUserRecord(payload);
}
